import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const brand = "#800b33";

const secondsRemaining = (timestamp) => {
  if (!timestamp) return 0;
  return Math.max(Math.ceil((new Date(timestamp).getTime() - Date.now()) / 1000), 0);
};

const formatCountdown = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export default function LoanOtpDialog({
  open,
  otpState,
  error,
  verifying,
  resending,
  onClose,
  onVerify,
  onResend,
}) {
  const [otpCode, setOtpCode] = useState("");
  const [tick, setTick] = useState(Date.now());

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setOtpCode("");
      return;
    }
    setOtpCode("");
  }, [open, otpState?.pending_request_id]);

  const expirySeconds = useMemo(() => secondsRemaining(otpState?.expires_at), [otpState?.expires_at, tick]);
  const resendSeconds = useMemo(
    () => secondsRemaining(otpState?.resend_available_at),
    [otpState?.resend_available_at, tick]
  );

  const attemptsLeft = Number(otpState?.attempts_left ?? 0);
  const helperText = attemptsLeft
    ? `${attemptsLeft} attempt${attemptsLeft === 1 ? "" : "s"} left`
    : "No attempts left";

  return (
    <Dialog open={open} onClose={verifying || resending ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 800 }}>Verify loan request</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Alert severity="info" variant="outlined">
            Enter the 6-digit OTP sent to your registered contact{otpState?.delivery_channels?.length > 1 ? "s" : ""}. Do not share this OTP with anyone.
          </Alert>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(otpState?.delivery_channels || []).map((channel) => (
              <Chip
                key={`${channel.channel}-${channel.destination}`}
                label={`${channel.channel}: ${channel.destination}`}
                variant="outlined"
                size="small"
              />
            ))}
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 1.5,
              backgroundColor: "#faf5f7",
              borderRadius: 2,
              p: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                OTP expires in
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: brand }}>
                {formatCountdown(expirySeconds)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Resend available in
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: brand }}>
                {formatCountdown(resendSeconds)}
              </Typography>
            </Box>
          </Box>

          <TextField
            label="OTP code"
            value={otpCode}
            onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
            autoFocus
            fullWidth
            helperText={helperText}
          />

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={verifying || resending}>
          Cancel
        </Button>
        <Button
          onClick={onResend}
          disabled={resending || resendSeconds > 0 || !(attemptsLeft > 0)}
        >
          {resending ? "Sending..." : resendSeconds > 0 ? `Resend in ${formatCountdown(resendSeconds)}` : "Resend OTP"}
        </Button>
        <Button
          variant="contained"
          onClick={() => onVerify(otpCode)}
          disabled={verifying || otpCode.length !== 6 || expirySeconds <= 0 || !(attemptsLeft > 0)}
          sx={{ bgcolor: brand, "&:hover": { bgcolor: "#6c0a2b" } }}
        >
          {verifying ? "Verifying..." : "Verify OTP"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
